import type { BrandDetail } from "./brand-display";

type Settings = BrandDetail["settings"];

/** Weekdays as the API numbers them (0 = Sunday), Monday first as people read a week. */
export const WEEK = [
  { day: 1, label: "Mon" },
  { day: 2, label: "Tue" },
  { day: 3, label: "Wed" },
  { day: 4, label: "Thu" },
  { day: 5, label: "Fri" },
  { day: 6, label: "Sat" },
  { day: 0, label: "Sun" },
];
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const PINCODE = /^[1-9]\d{5}$/;

export interface RulesForm {
  dayPauseCutoffTime: string;
  weeklyOffDays: number[];
  allowFullDayPause: boolean;
  allowSingleMealPause: boolean;
  maxPauseDays: string;
  maxPauseMeals: string;
  autoConfirmHours: string;
  pincodes: string;
}

const limitOf = (text: string): number | null | undefined => {
  const t = text.trim();
  if (!t) return null;
  return /^\d+$/.test(t) && Number(t) <= 365 ? Number(t) : undefined;
};

/** Checks the rules the way the API does; returns the body, or the problems by field. */
export function rulesBody(
  form: RulesForm,
): { body: Settings } | { errors: Partial<Record<keyof RulesForm, string>> } {
  const errors: Partial<Record<keyof RulesForm, string>> = {};
  if (!TIME.test(form.dayPauseCutoffTime)) errors.dayPauseCutoffTime = "A time like 21:00.";
  const maxPauseDays = limitOf(form.maxPauseDays);
  if (maxPauseDays === undefined) errors.maxPauseDays = "A whole number, or empty for no limit.";
  const maxPauseMeals = limitOf(form.maxPauseMeals);
  if (maxPauseMeals === undefined) errors.maxPauseMeals = "A whole number, or empty for no limit.";
  const hours = Number(form.autoConfirmHours);
  if (!/^\d+$/.test(form.autoConfirmHours.trim()) || hours < 1 || hours > 168)
    errors.autoConfirmHours = "Whole hours, 1 to 168.";
  const pincodes = form.pincodes.split(/[\s,]+/).filter(Boolean);
  const wrong = pincodes.filter((p) => !PINCODE.test(p));
  if (wrong.length) errors.pincodes = `Not a PIN code: ${wrong.join(", ")}`;
  if (Object.keys(errors).length) return { errors };
  return {
    body: {
      dayPauseCutoffTime: form.dayPauseCutoffTime,
      weeklyOffDays: [...form.weeklyOffDays].sort((a, b) => a - b),
      allowFullDayPause: form.allowFullDayPause,
      allowSingleMealPause: form.allowSingleMealPause,
      maxPauseDays: maxPauseDays ?? null,
      maxPauseMeals: maxPauseMeals ?? null,
      autoConfirmHours: hours,
      serviceablePincodes: [...new Set(pincodes)].sort(),
    },
  };
}
